import React, { useState } from 'react';
import {
  TextField,
  FormControl,
  Typography,
  IconButton,
  Chip,
  Grid,
  Box,
  Autocomplete,
  Popper,
  createFilterOptions,
  Stack,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Slider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PopperProps } from '@mui/material/Popper';
import { format, isValid, parseISO } from 'date-fns';
import { getUserLocale } from '../../common/utils/common.util';

export type FilterConfigLookupOption = { value: string | number; label: string };

export interface BooleanStateNames {
  on: string;
  off: string;
}

export interface FilterConfig {
  group?: string;
  onDisplay?: boolean;
  propertyName: string;
  propertyDisplayName: string;
  type: 'number' | 'date' | 'text' | 'lookup' | 'boolean';
  multiSelect?: boolean;
  nested?: boolean;
  rangeMin?: number;
  rangeMax?: number;
  lookupOptions?: FilterConfigLookupOption[]; // Optional for 'lookup' type
  displayedOptionsLimit?: number; // Optional for 'lookup' type
  booleanStateNames?: BooleanStateNames; // Optional for 'boolean' type
}

type FilterConfigs = FilterConfig[];

// Custom Popper component with increased z-index
export const CustomPopper = React.forwardRef<HTMLDivElement, PopperProps>((props, ref) => {
  return <Popper {...props} style={{ zIndex: 1500 }} />;
});

CustomPopper.displayName = 'CustomPopper';

const DynamicFilters: React.FC<{
  filtersConfig: FilterConfigs;
  filterValues: any;
  onFilterChange: (name: string, value: any) => void;
  resetFilter: (propertyName: string) => void;
}> = ({ filtersConfig, filterValues, onFilterChange, resetFilter }) => {
  const [tempTextValue, setTempTextValue] = useState<string>('');

  const handleAddTextValue = (propertyName: string) => {
    const updatedValues = filterValues[propertyName] ? [...filterValues[propertyName], tempTextValue] : [tempTextValue];
    onFilterChange(propertyName, updatedValues);
    setTempTextValue('');
  };

  const handleRemoveTextValue = (propertyName: string, valueToRemove: string) => {
    const updatedValues = filterValues[propertyName].filter((value: string) => value !== valueToRemove);
    onFilterChange(propertyName, updatedValues);
  };

  const handleDateChange = (propertyName: string, selectedDate: number | Date | null, isStartDate: boolean) => {
    if (!selectedDate) {
      onFilterChange(propertyName, {
        ...filterValues[propertyName],
        [isStartDate ? 'start' : 'end']: null,
      });
      return;
    }

    const dateObj = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);

    if (!dateObj || !isValid(dateObj)) {
      console.error('Invalid date:', selectedDate);
      onFilterChange(propertyName, {
        ...filterValues[propertyName],
        [isStartDate ? 'start' : 'end']: null,
      });
      return;
    }

    // Extract only the date (YYYY-MM-DD)
    const dateOnly = format(dateObj, 'yyyy-MM-dd');
    const dateOnlyUTC = `${dateOnly}T00:00:00.000Z`;

    onFilterChange(propertyName, {
      ...filterValues[propertyName],
      [isStartDate ? 'start' : 'end']: dateOnlyUTC,
    });
  };

  const renderFilter = (config: FilterConfig) => {
    switch (config.type) {
      case 'text':
        if (config.multiSelect) {
          const handleKeyPress = (event: any) => {
            if (event.key === 'Enter' && tempTextValue.trim() !== '') {
              handleAddTextValue(config.propertyName);
            }
          };

          return (
            <div key={config.propertyName}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label={config.propertyDisplayName}
                  placeholder='Type and Press Enter'
                  value={tempTextValue}
                  onChange={e => setTempTextValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  fullWidth
                  margin='normal'
                />
              </div>
              <div>
                {filterValues[config.propertyName]?.map((value: string, index: number) => (
                  <Chip key={index} label={value} onDelete={() => handleRemoveTextValue(config.propertyName, value)} />
                ))}
              </div>
            </div>
          );
        } else {
          return (
            <TextField
              key={config.propertyName}
              label={config.propertyDisplayName}
              value={filterValues[config.propertyName] || ''}
              onChange={e => onFilterChange(config.propertyName, e.target.value)}
              fullWidth
              margin='normal'
            />
          );
        }
      case 'date':
        return (
          <LocalizationProvider key={config.propertyName} dateAdapter={AdapterDateFns} adapterLocale={getUserLocale()}>
            <Grid container alignItems='center' justifyContent='space-between' spacing={1} mt={2}>
              <Grid size={{ xs: 5 }}>
                <DatePicker
                  label='From Date'
                  value={filterValues[config.propertyName]?.start ? parseISO(filterValues[config.propertyName]?.start) : null}
                  onChange={newStartDate => handleDateChange(config.propertyName, newStartDate, true)}
                  slots={{ popper: CustomPopper }}
                />
              </Grid>
              <Grid size={{ xs: 1 }} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography
                  variant='subtitle1'
                  style={{
                    transform: 'scaleX(2)', // Stretch the hyphen along the X-axis
                    display: 'inline-block', // Ensures transform applies correctly
                  }}
                >
                  -
                </Typography>
              </Grid>
              <Grid size={{ xs: 5 }}>
                <DatePicker
                  label='To Date'
                  value={filterValues[config.propertyName]?.end ? parseISO(filterValues[config.propertyName]?.end) : null}
                  onChange={newEndDate => handleDateChange(config.propertyName, newEndDate, false)}
                  slots={{ popper: CustomPopper }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        );
      case 'number': {
        const range = filterValues[config.propertyName] || [config.rangeMin || 0, config.rangeMax || 100];

        const handleRangeChange = (event: any, newValue: number | number[]) => {
          onFilterChange(config.propertyName, newValue);
        };

        const handleMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const newMin = Number(event.target.value) || 0;
          const newRange = [newMin, Math.max(newMin, range[1])]; // Ensure min is not greater than max
          onFilterChange(config.propertyName, newRange);
        };

        const handleMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const newMax = Number(event.target.value) || 0;
          const newRange = [Math.min(range[0], newMax), newMax]; // Ensure max is not less than min
          onFilterChange(config.propertyName, newRange);
        };

        return (
          <div key={config.propertyName}>
            {/* <Typography gutterBottom>{config.propertyDisplayName}</Typography> */}
            <Slider
              value={range}
              onChange={handleRangeChange}
              valueLabelDisplay='auto'
              aria-labelledby='range-slider'
              min={config.rangeMin || 0}
              max={config.rangeMax || 100}
            />
            <Grid container spacing={2} justifyContent='center' alignItems='center'>
              <Grid size='auto'>
                <TextField
                  label='Min'
                  type='number'
                  value={range[0]}
                  onChange={handleMinChange}
                  inputProps={{ min: config.rangeMin || 0, max: range[1] }}
                />
              </Grid>
              <Grid size='auto'>
                <Typography variant='subtitle1'>-</Typography>
              </Grid>
              <Grid size='auto'>
                <TextField
                  label='Max'
                  type='number'
                  value={range[1]}
                  onChange={handleMaxChange}
                  inputProps={{ min: range[0], max: config.rangeMax || 100 }}
                />
              </Grid>
            </Grid>
          </div>
        );
      }
      case 'lookup': {
        const selectedLookupValues = filterValues[config.propertyName] || [];
        const filterOptions = createFilterOptions<FilterConfigLookupOption>({
          matchFrom: 'start',
          limit: config?.displayedOptionsLimit ?? (config.lookupOptions || [])?.length,
        });

        return (
          <div key={config.propertyName}>
            <FormControl fullWidth margin='normal' sx={{ marginTop: 2 }}>
              <Autocomplete<FilterConfigLookupOption, true, false, false>
                multiple
                id={`autocomplete-${config.propertyName}`}
                options={config.lookupOptions || []}
                filterOptions={filterOptions}
                autoComplete
                autoHighlight
                getOptionLabel={(option: FilterConfigLookupOption) => option.label}
                value={selectedLookupValues
                  .map((val: any) => config.lookupOptions?.find(opt => opt.value === val))
                  .filter((opt: any): opt is FilterConfigLookupOption => !!opt)}
                onChange={(event, newValue) => {
                  onFilterChange(config.propertyName, Array.isArray(newValue) ? newValue.map(item => item.value) : []);
                }}
                filterSelectedOptions
                renderTags={(value: readonly FilterConfigLookupOption[], getTagProps) =>
                  value.map((option, index) => (
                    // eslint-disable-next-line react/jsx-key
                    <Chip label={option.label} {...getTagProps({ index })} style={{ backgroundColor: 'lightgray', color: 'black' }} />
                  ))
                }
                renderInput={params => <TextField {...params} label={config.propertyDisplayName} placeholder='Select multiple' />}
              />
            </FormControl>
          </div>
        );
      }

      case 'boolean': {
        const handleToggle = (event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
          const mappedValue = newValue === 'true' ? true : newValue === 'false' ? false : null;
          onFilterChange(config.propertyName, mappedValue);
        };
        const toggleButtonStyles = { textTransform: 'none', fontWeight: 'bold' };
        const value = filterValues[config.propertyName];
        return (
          <ToggleButtonGroup
            value={value === null || value === undefined ? 'null' : value.toString()} // Map null to 'null'
            exclusive
            onChange={handleToggle}
            fullWidth
            color='primary'
            aria-label='tri-state toggle'
          >
            <ToggleButton value='null' aria-label='not applied' sx={toggleButtonStyles}>
              All
            </ToggleButton>
            <ToggleButton value='true' aria-label='on' sx={toggleButtonStyles}>
              {config.booleanStateNames?.on ?? 'On'}
            </ToggleButton>
            <ToggleButton value='false' aria-label='off' sx={toggleButtonStyles}>
              {config.booleanStateNames?.off ?? 'Off'}
            </ToggleButton>
          </ToggleButtonGroup>
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      {filtersConfig.map(config => {
        const isTextField = config.type == 'text';
        return (
          <Stack key={config.propertyName} flexDirection={'row'} spacing={1} alignItems={isTextField ? 'top' : 'center'}>
            <Box flex={5}>{renderFilter(config)}</Box>
            <Box flex={0.5} pl={1} pt={isTextField ? 2 : undefined}>
              <Tooltip title='Clear filter' arrow={false}>
                <IconButton aria-label='close' onClick={() => resetFilter(config.propertyName)} size='small'>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        );
      })}
    </>
  );
};

function formatDate(date: Date): string {
  return date.toISOString();
}

export default DynamicFilters;
